// TherapistPayHistoryPage.jsx
import React, { useState, useEffect } from 'react';
import API from '../services/api';

// A small helper to format ISO strings like 2025-03-02T05:00:00.000Z
function formatDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

function TherapistPayHistoryPage() {
  const [payrolls, setPayrolls] = useState([]);  // All approved pay periods
  const [detail, setDetail] = useState(null);    // Current pay period detail
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPayrolls();
  }, []);

  // 1) Fetch all approved pay periods
  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      // calls GET /therapist/payroll
      const res = await API.get('/therapist/payroll');
      setPayrolls(res.data);
    } catch (err) {
      console.error('Error fetching pay periods', err);
      alert('Error fetching pay periods');
    } finally {
      setLoading(false);
    }
  };

  // 2) Load the session + bonus detail for a single pay period
  //    plus the total_collected & therapist_pay
  const viewDetail = async (payrollId) => {
    try {
      setLoading(true);
      // calls GET /therapist/payroll/:payrollId
      const res = await API.get(`/therapist/payroll/${payrollId}`);
      // shape => { total_collected, therapist_pay, sessions: [...] }
      setDetail({
        payrollId,
        total_collected: res.data.total_collected || 0,
        therapist_pay: res.data.therapist_pay || 0,
        sessions: res.data.sessions || []
      });
    } catch (err) {
      console.error('Error fetching payroll detail', err);
      alert('Error fetching payroll detail');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ margin: '2rem' }}>
      <h2>My Pay Period History</h2>

      {loading && <p>Loading...</p>}

      {!loading && payrolls.length === 0 && (
        <p>No approved pay periods found.</p>
      )}

      {/* List of pay periods */}
      {payrolls.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {payrolls.map((p) => {
            const startFmt = formatDate(p.start_date);
            const endFmt = formatDate(p.end_date);
            return (
              <li key={p.id} style={{ marginBottom: '1rem' }}>
                <button onClick={() => viewDetail(p.id)}>
                  {startFmt} - {endFmt}
                </button>
                {'  '}
                <span style={{ marginLeft: '1rem' }}>
                  Total Collected: ${p.total_collected}
                </span>
                {'  '}
                <span style={{ marginLeft: '1rem' }}>
                  Therapist Pay: ${parseFloat(p.therapist_pay || 0).toFixed(2)}
                </span>
                {p.approved_at && (
                  <span style={{ marginLeft: '1rem', color: 'green' }}>
                    Approved on {new Date(p.approved_at).toLocaleString()}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Detailed table for chosen pay period */}
      {detail && (
        <div style={{ marginTop: '2rem' }}>
          <h3>Sessions & Bonuses for Pay Period #{detail.payrollId}</h3>

          {/* Display total_collected and therapist_pay at top */}
          <p>
            <strong>Total Collected:</strong> $
            {parseFloat(detail.total_collected).toFixed(2)}
          </p>
          <p>
            <strong>My Pay (No Split on Bonus):</strong> $
            {parseFloat(detail.therapist_pay).toFixed(2)}
          </p>

          {detail.sessions.length === 0 ? (
            <p>No sessions or bonuses found in this payroll.</p>
          ) : (
            <table border="1" cellPadding="5" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client / Description</th>
                  <th>Billed</th>
                  <th>Collected</th>
                </tr>
              </thead>
              <tbody>
                {detail.sessions.map((item, idx) => {
                  const dateDisplay = formatDate(item.session_date);
                  return (
                    <tr key={idx}>
                      <td>{dateDisplay}</td>
                      <td>
                        {item.record_type === 'SESSION'
                          ? item.client_name
                          : 'Productivity Bonus'}
                      </td>
                      <td>
                        ${parseFloat(item.billed || 0).toFixed(2)}
                      </td>
                      <td>
                        ${parseFloat(item.collected || 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default TherapistPayHistoryPage;
